// Onay kutulu ağaç kabuğu (CatalogCheckboxTree + PackageMembersTree ortak): `role=tree/treeitem`,
// `aria-level/-expanded/-checked`, gezinme odağı tek satırda (`tabIndex=0`, kimlik bazlı) ve
// ↑/↓ Home/End, →/←, Space (DR17-2). Satır 40 px; satırın metin kısmı onay kutusunu saran `<label>`
// (DR16), aç/kapat oku ayrı 44×44 hedef; satır sonu eylemleri label DIŞINDA (iç içe etkileşim yok).
// > 80 satırda `use-virtual-rows` penceresi (DR17-6): kap kendi dikey kaydırıcısıdır, odaklı satır
// pencere dışına kaysa da DOM'da kalır (`windowSegments`). ≤ 80 satırda kap yok, tüm satırlar.

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  ariaPositions,
  renderedIndices,
  resolveFocusId,
  treeKeyAction,
  windowSegments,
  type TreeViewRow,
} from "../../lib/kpi-tree-view";
import type { TriState } from "../../lib/kpi-set-editor";
import { treeIndent } from "../../lib/kpi-tree-list";
import { hitArea44 } from "./kpi-form-styles";
import { useVirtualRows } from "./use-virtual-rows";

export const CHECK_TREE_ROW_HEIGHT = 40;
/** Aç/kapa okunun yerleşimdeki genişliği: 44 px isabet alanı − 2×6 px negatif kenar. */
export const CHECK_TREE_TOGGLE_LAYOUT_WIDTH = 32;

export interface CheckTreeRow extends TreeViewRow {
  depth: number;
  /** null → onay kutusu yok (salt okunur ata satırı) */
  check: TriState | null;
  /** dolu → kutu pasif; neden görünür metin + `aria-describedby` (yalnız `title` değil) */
  disabledReason?: string | null;
  /** true → satır hatalı (`data-error`, kırmızımsı zemin) — ör. kayıt reddi sonrası satır vurgusu */
  error?: boolean | undefined;
  /** erişilebilir ad + kutu etiketi */
  label: string;
  weight?: 400 | 600 | 700 | undefined;
  /** etiket yanındaki içerik (kod, rozetler) — label içinde */
  content?: ReactNode;
  /** dolu → etiket tek başına kırpılır; meta (kod, rozet, sayaç) sağda küçülmeyen sütundur */
  meta?: ReactNode;
  /** satır sonu eylemleri — label dışında; işlev → yalnız odaklı satırda Tab durağı verilebilir */
  trailing?: ReactNode | ((focused: boolean) => ReactNode);
  background?: string | undefined;
  /** true → satır metni seçer (onay kutusu ayrı hedef); `CheckTreeProps.onSelect` ister */
  selectable?: boolean | undefined;
  /** true → yalnız onay kutusu işaretler; metne tıklama işaretlemez (ör. kategori toplu çıkarma) */
  checkOnly?: boolean | undefined;
}

export interface CheckTreeProps {
  rows: readonly CheckTreeRow[];
  ariaLabel: string;
  onToggleExpand: (id: string, open: boolean) => void;
  onCheck: (id: string) => void;
  /** sanallaştırılmış kabın en büyük yüksekliği (yalnız > 80 satırda; `alwaysScroll` → her zaman) */
  maxHeight?: number | string | undefined;
  /** `selectable` satırın metnine tıklama / Enter */
  onSelect?: ((id: string) => void) | undefined;
  selectedId?: string | null | undefined;
  /**
   * Kap her zaman kendi kaydırıcısı (çağıran ayrıca `overflow` sarmalayıcısı kurmaz — tek kaydırıcı).
   * Verilmezse kaydırma yalnız sanal pencerede; ≤ 80 satırda dış kap kaydırır.
   */
  alwaysScroll?: boolean | undefined;
  testId?: string | undefined;
  /** dar ekran: seviye başına 14 px girinti (varsayılan 22) */
  narrow?: boolean | undefined;
  /**
   * false → pasif neden satırda görünmez (ekran okuyucuya `aria-describedby` ile gider). Bütün ağacın
   * pasif olduğu durumda (salt okunur paket) tekrar eden metin yerine sayfa bandı söyler.
   */
  reasonVisible?: boolean | undefined;
}

function TriBox(p: { state: TriState; disabled: boolean; onToggle: () => void }): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = p.state === "some";
  }, [p.state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      tabIndex={-1}
      aria-hidden="true"
      checked={p.state === "all"}
      disabled={p.disabled}
      onChange={p.onToggle}
      style={{ width: 16, height: 16, cursor: p.disabled ? "default" : "pointer", flexShrink: 0 }}
    />
  );
}

export function CheckTree(props: CheckTreeProps): JSX.Element {
  const { rows, onToggleExpand, onCheck } = props;
  const [focusId, setFocusId] = useState<string | null>(null);
  const focused = resolveFocusId(rows, focusId);
  const focusIndex = focused === null ? null : rows.findIndex((r) => r.id === focused);
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const moveFocus = useRef(false);
  // hook tablo hücresi odak yolunu da sunar; ağaç kendi odağını yönetir (odaklı satır hep DOM'da)
  const unusedCellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const virtual = useVirtualRows(rows.length, unusedCellRefs, CHECK_TREE_ROW_HEIGHT);
  const treeId = useId();
  const positions = useMemo(() => ariaPositions(rows), [rows]);
  const segments = useMemo(
    () => windowSegments(virtual.window, rows.length, focusIndex, CHECK_TREE_ROW_HEIGHT),
    [virtual.window, rows.length, focusIndex],
  );

  useEffect(() => {
    if (!moveFocus.current || focused === null) return;
    moveFocus.current = false;
    const el = itemRefs.current.get(focused);
    el?.focus({ preventScroll: true });
    el?.scrollIntoView?.({ block: "nearest" });
  });

  function onKeyDown(e: KeyboardEvent<HTMLUListElement>): void {
    // satır içi düğmelerden (Varsayılanlar) köpüren tuşlar ağaç gezinmesi değildir
    if (!(e.target instanceof HTMLElement) || e.target.getAttribute("role") !== "treeitem") return;
    if (e.key === "Enter" && props.onSelect) {
      const cur = rows.find((r) => r.id === focused);
      if (cur?.selectable) {
        e.preventDefault();
        props.onSelect(cur.id);
        return;
      }
    }
    const action = treeKeyAction(rows, focused, e.key);
    if (!action) return;
    e.preventDefault();
    if (action.kind === "focus") {
      moveFocus.current = true;
      setFocusId(action.id);
    } else if (action.kind === "expand") onToggleExpand(action.id, action.open);
    else {
      const row = rows.find((r) => r.id === action.id);
      if (row && !row.disabledReason) onCheck(action.id);
    }
  }

  const active = virtual.window.active;
  const list = (
    <ul
      role="tree"
      aria-label={props.ariaLabel}
      data-testid={props.testId}
      onKeyDown={onKeyDown}
      style={{ listStyle: "none", margin: 0, padding: 0 }}
    >
      {segments.map((seg, si) =>
        seg.kind === "pad" ? (
          <li
            key={`pad-${si.toString()}`}
            role="none"
            aria-hidden="true"
            style={{ height: seg.height }}
          />
        ) : (
          rows.slice(seg.start, seg.end).map((r, k) => (
            <TreeItem
              key={r.id}
              row={r}
              pos={positions[seg.start + k]!}
              reasonId={`${treeId}-reason-${(seg.start + k).toString()}`}
              reasonVisible={props.reasonVisible !== false}
              narrow={props.narrow === true}
              selected={r.id === props.selectedId}
              {...(props.onSelect ? { onSelect: props.onSelect } : {})}
              focused={r.id === focused}
              fixedHeight={active}
              itemRef={(el) => {
                if (el) itemRefs.current.set(r.id, el);
                else itemRefs.current.delete(r.id);
              }}
              onFocus={() => setFocusId(r.id)}
              onToggleExpand={onToggleExpand}
              onCheck={onCheck}
            />
          ))
        ),
      )}
    </ul>
  );
  // Kök öğe iki kipte de AYNI (div > ul): eşik geçişinde (aç/kapa) alt ağaç yeniden bağlanmaz,
  // DOM odağı body'ye düşmez (DR17-2/6). Kaydırma/işaret yalnız gerektiğinde.
  const scroll = active || props.alwaysScroll === true;
  return (
    <div
      ref={virtual.containerRef}
      onScroll={virtual.onScroll}
      data-check-tree=""
      {...(active ? { "data-virtual-rows": renderedIndices(segments).length } : {})}
      style={{
        width: "100%",
        minWidth: 0,
        ...(scroll ? { maxHeight: props.maxHeight ?? 320, overflowY: "auto" as const } : {}),
      }}
    >
      {list}
    </div>
  );
}

const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

function TreeItem(p: {
  row: CheckTreeRow;
  pos: { setSize: number; posInSet: number };
  reasonId: string;
  reasonVisible: boolean;
  narrow: boolean;
  selected: boolean;
  onSelect?: (id: string) => void;
  focused: boolean;
  fixedHeight: boolean;
  itemRef: (el: HTMLLIElement | null) => void;
  onFocus: () => void;
  onToggleExpand: (id: string, open: boolean) => void;
  onCheck: (id: string) => void;
}): JSX.Element {
  const { row: r } = p;
  const disabled = !!r.disabledReason;
  const hasMeta = r.meta !== undefined && r.meta !== null;
  const text = hasMeta ? (
    <>
      <span
        className="min-w-0 flex-1 truncate"
        title={r.label}
        style={{ fontWeight: r.weight ?? 400, fontSize: "1rem" }}
      >
        {r.label}
      </span>
      <span
        data-tree-meta=""
        className="flex items-center"
        style={{ flexShrink: 0, whiteSpace: "nowrap", gap: 6 }}
      >
        {r.meta}
      </span>
    </>
  ) : (
    <span
      className="min-w-0 flex-1 truncate"
      style={{ fontWeight: r.weight ?? 400, fontSize: "1rem" }}
    >
      {r.label}
      {r.content}
    </span>
  );
  const selectable = r.selectable === true && p.onSelect !== undefined;
  const splitCheck = selectable || r.checkOnly === true;
  return (
    <li
      ref={p.itemRef}
      role="treeitem"
      aria-level={r.depth + 1}
      aria-setsize={p.pos.setSize}
      aria-posinset={p.pos.posInSet}
      {...(disabled ? { "aria-describedby": p.reasonId } : {})}
      {...(r.error ? { "data-error": "true", "aria-invalid": true } : {})}
      {...(r.hasChildren ? { "aria-expanded": r.expanded } : {})}
      {...(r.check !== null
        ? { "aria-checked": r.check === "all" ? true : r.check === "some" ? "mixed" : false }
        : {})}
      {...(disabled ? { "aria-disabled": true } : {})}
      {...(selectable ? { "aria-selected": p.selected } : {})}
      tabIndex={p.focused ? 0 : -1}
      onFocus={(e) => {
        if (e.target === e.currentTarget) p.onFocus();
      }}
      onMouseDown={(e) => {
        // tıklama sonrası ok tuşları çalışsın: odak iç kutuya/oka değil satıra (satır sonu eylemleri hariç)
        if ((e.target as HTMLElement).closest("[data-tree-trailing]")) return;
        e.preventDefault();
        e.currentTarget.focus({ preventScroll: true });
        p.onFocus();
      }}
      className="flex items-center gap-2"
      data-selected={p.selected || undefined}
      style={{
        paddingRight: 12,
        paddingLeft: 4 + treeIndent(r.depth, p.narrow),
        borderBottom: "1px solid var(--color-border)",
        ...(p.fixedHeight
          ? { height: CHECK_TREE_ROW_HEIGHT, boxSizing: "border-box", overflow: "hidden" }
          : { minHeight: CHECK_TREE_ROW_HEIGHT }),
        background: r.error
          ? "color-mix(in srgb, var(--color-error) 8%, white)"
          : p.selected
            ? "color-mix(in srgb, var(--color-primary) 8%, white)"
            : (r.background ?? "transparent"),
        outlineOffset: -2,
      }}
    >
      {r.hasChildren ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={r.expanded ? "Kapat" : "Aç"}
          onClick={() => p.onToggleExpand(r.id, !r.expanded)}
          style={{ ...hitArea44, margin: "-2px -6px", color: "var(--color-text-2)", flexShrink: 0 }}
        >
          {r.expanded ? "▾" : "▸"}
        </button>
      ) : (
        <span style={{ width: CHECK_TREE_TOGGLE_LAYOUT_WIDTH, flexShrink: 0 }} aria-hidden="true" />
      )}
      {r.check !== null && splitCheck ? (
        <>
          <label
            title={r.disabledReason ?? undefined}
            className="flex items-center"
            style={{ cursor: disabled ? "default" : "pointer", minHeight: 36, minWidth: 24 }}
          >
            <TriBox state={r.check} disabled={disabled} onToggle={() => p.onCheck(r.id)} />
          </label>
          <div
            className="flex min-w-0 flex-1 items-center gap-2"
            data-tree-select=""
            style={{ cursor: selectable ? "pointer" : "default", minHeight: 36 }}
            onClick={selectable ? () => p.onSelect?.(r.id) : undefined}
          >
            {text}
          </div>
        </>
      ) : r.check !== null ? (
        <label
          className="flex min-w-0 flex-1 items-center gap-2"
          title={r.disabledReason ?? undefined}
          style={{ cursor: disabled ? "default" : "pointer", minHeight: 36 }}
        >
          <TriBox state={r.check} disabled={disabled} onToggle={() => p.onCheck(r.id)} />
          {text}
        </label>
      ) : (
        text
      )}
      {disabled ? (
        <span
          id={p.reasonId}
          data-tree-reason=""
          style={
            p.reasonVisible
              ? { fontSize: "0.75rem", color: "var(--color-text-3)", flexShrink: 0 }
              : SR_ONLY
          }
        >
          {r.disabledReason}
        </span>
      ) : null}
      {r.trailing ? (
        <span data-tree-trailing="" className="flex items-center">
          {typeof r.trailing === "function" ? r.trailing(p.focused) : r.trailing}
        </span>
      ) : null}
    </li>
  );
}
